import {Helmet} from '@dr.pogodin/react-helmet';
import {BreadcrumbType, Form, FormField, FormSubmitHandler, TextInput} from 'platform/components';
import {Grid, VStack} from 'platform/foundation';
import {object} from 'yup';

import {DeepPartial} from 'react-hook-form';
import {useParams} from 'react-router-dom';

import {
  useGetConfigurationOperationIdDefaultQuery,
  useGetOperationItemsQuery,
  usePatchConfigurationOperationIdDefaultMutation,
} from '@dms/api/finance';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsFooter, SettingsSection, SettingsTemplate} from '@dms/settings/shared';
import {handleApiError} from '@dms/shared';

import {ApiError, useNavigate, yupString} from 'shared/web';

type FormValues = {
  itemTypeCode: string;
  value: string;
};

const breadcrumbs: BreadcrumbType[] = [
  {
    label: i18n.t('page.accounting.labels.operationDefaults.title'),
    href: settingsRoutes.accountingOperationDefaultsOverview,
  },
];

export function OperationDefaultsDetail() {
  const navigate = useNavigate();
  const {id: operationDefaultId} = useParams();
  const title = i18n.t('page.accounting.labels.operationDefaults.detail.title');

  const {data: operationData, isLoading: isLoadingOperation} =
    useGetConfigurationOperationIdDefaultQuery(
      {id: operationDefaultId || ''},
      {skip: !operationDefaultId}
    );
  const {data: operationItems} = useGetOperationItemsQuery({application: 'workshop'});
  const [updateItem, {isLoading: isFetchingUpdateItem}] =
    usePatchConfigurationOperationIdDefaultMutation();

  const operationItemsList: {value: string | null; label: string | null}[] =
    operationItems
      ?.filter((item) => item.operationId && item.itemCodeName)
      .map((item) => ({
        value: item.operationId || null,
        label: `${item.operationId} - ${item.itemCodeName}` || null,
      })) || [];

  const defaultValues: DeepPartial<FormValues> = {
    itemTypeCode: operationData?.operationIdDefault?.itemTypeCode ?? undefined,
    value: operationData?.operationIdDefault?.value ?? undefined,
  };

  const handleSubmit: FormSubmitHandler<FormValues> = async (
    data,
    _setErrors,
    _reset,
    blockerContext
  ) => {
    if (!operationDefaultId || !data.value) {
      return;
    }

    await updateItem({
      id: operationDefaultId,
      operationId: data.value,
    })
      .unwrap()
      .then(() => {
        if (blockerContext?.wasSubmittedFromBlocker) {
          return;
        }
        navigate(settingsRoutes.accountingOperationDefaultsOverview);
      })
      .catch((e: ApiError) =>
        handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
      );
  };

  return (
    <>
      <Helmet title={title} />
      <SettingsTemplate
        header={{
          title,
          breadcrumbs,
        }}
        data-testid={testIds.settings.accountingOperationDefaultsDetail('detail')}
        isLoading={isLoadingOperation}
      >
        <Form<FormValues>
          schema={schema}
          defaultValues={defaultValues}
          onSubmit={handleSubmit}
          shouldWatchForUnsavedChanges
        >
          {(control, _formApi, navigateWithoutBlocker) => (
            <VStack spacing={4}>
              <SettingsSection>
                <Grid columns={2} align="flex-end">
                  <TextInput
                    type="text"
                    name="itemTypeName"
                    value={operationData?.operationIdDefault?.itemTypeName || ''}
                    label={i18n.t('page.accounting.labels.operationDefaults.typeName')}
                    isDisabled
                    data-testid={testIds.settings.accountingOperationDefaultsDetail('typeName')}
                  />
                  <FormField
                    control={control}
                    type="choice"
                    isNotClearable
                    options={operationItemsList}
                    name="value"
                    label={i18n.t('page.accounting.labels.operationDefaults.operationValue')}
                    isRequired
                    data-testid={testIds.settings.accountingOperationDefaultsDetail(
                      'operationValue'
                    )}
                  />
                </Grid>
              </SettingsSection>
              <SettingsFooter
                actions={[
                  {
                    type: 'button',
                    title: i18n.t('general.actions.discardChanges'),
                    onClick: () =>
                      navigateWithoutBlocker(settingsRoutes.accountingOperationDefaultsOverview),
                    variant: 'secondary',
                  },
                  {
                    type: 'form-button',
                    control,
                    isLoading: isFetchingUpdateItem,
                    buttonType: 'submit',
                    title: i18n.t('general.actions.saveChanges'),
                  },
                ]}
              />
            </VStack>
          )}
        </Form>
      </SettingsTemplate>
    </>
  );
}

const schema = object({
  itemTypeCode: yupString.required(),
  value: yupString.required(),
});
